import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import React from "react";
import axios from "axios";
import Appointment from "../interface/appointment";
import { AppointmentOverviewContext } from "../context/appointmentOverviewContext";
import { AppContext, User } from "../../../context/appContext";
import { endpoints } from "../../../endpoints/endpoints";
import transformDateToDe from "../../../functions/transformDateToDe";

interface StatusOption {
  label: string;
  value: number;
}

const STATUS_OPTIONS: StatusOption[] = [
  { label: "Stornierung", value: 0 },
  { label: "Kulanz", value: 1 },
  { label: "Versäumt", value: 2 },
  { label: "Gebucht", value: 3 },
];

const getStatusLabel = (status: string): string => {
  switch (status) {
    case "G":
      return "Gebucht";
    case "V":
      return "Versäumt";
    case "K":
      return "Storniert (Kulanz)";
    default:
      return status;
  }
};

const getStatusColor = (
  status: string
): "error.main" | "warning.main" | "success.main" => {
  if (status === "V") return "error.main";
  if (status === "K") return "warning.main";
  return "success.main";
};

const AppointmentStatusControls = ({
  appointment,
  onChanged,
}: {
  appointment: Appointment;
  onChanged: () => void;
}): React.JSX.Element => {
  const { doStatusChange }: { doStatusChange: (id: number, option: StatusOption | null, onDone: () => void) => void } =
    React.useContext(AppointmentOverviewContext);

  const [selectedOption, setSelectedOption] =
    React.useState<StatusOption | null>(null);

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Autocomplete
        size="small"
        disablePortal
        isOptionEqualToValue={(option, value) => option.value === value.value}
        options={STATUS_OPTIONS}
        value={selectedOption}
        onChange={(_event, newValue) => setSelectedOption(newValue)}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => <TextField {...params} label="Aktion" />}
        sx={{ minWidth: 150 }}
      />
      <Button
        disabled={selectedOption === null}
        onClick={() =>
          doStatusChange(appointment.id, selectedOption, () => {
            setSelectedOption(null);
            onChanged();
          })
        }
        variant="contained"
        color="primary"
        size="small"
      >
        <CheckBoxOutlinedIcon />
      </Button>
    </Box>
  );
};

const RenderParticipantAppointments = ({
  participantId,
}: {
  participantId: number;
}): React.JSX.Element | null => {
  const {
    user,
    checkSession,
  }: { user: User; checkSession: (response: any) => void } =
    React.useContext(AppContext);

  const [participantAppointments, setParticipantAppointments] = React.useState<
    Appointment[]
  >([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  // Fetch ALL appointments of the participant (independent of the selected
  // time range) so the expanded tile lists every appointment, not only the
  // ones that happen to fall within the coach overview's date range.
  const fetchParticipantAppointments = React.useCallback((): void => {
    if (!user?.sessionId) {
      setParticipantAppointments([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    axios
      .post(endpoints.appointment.participantAppointmentsForStaff, {
        sessionId: user.sessionId,
        participantId,
      })
      .then((response) => {
        checkSession(response);

        const data: Appointment[] = response.data?.data ?? [];
        setParticipantAppointments(
          data.filter((a) => a.status !== "F")
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching participant appointments", error);
        setParticipantAppointments([]);
        setLoading(false);
      });
  }, [checkSession, participantId, user.sessionId]);

  React.useEffect(() => {
    fetchParticipantAppointments();
  }, [fetchParticipantAppointments]);

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
          <CircularProgress size={16} color="secondary" />
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            Termine werden geladen...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (participantAppointments.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="caption" sx={{ fontWeight: "bold" }}>
        Gebuchte Termine ({participantAppointments.length})
      </Typography>

      {participantAppointments.map((appointment, index) => (
        <React.Fragment key={appointment.id}>
          <Grid container spacing={1} sx={{ py: 1, alignItems: "center" }}>
            <Grid item xs={1}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", textAlign: "center" }}
              >
                {index + 1}.
              </Typography>
            </Grid>

            <Grid item xs={11} container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold" }}
                  component="p"
                >
                  Datum
                </Typography>
                <Typography variant="body2">
                  {transformDateToDe(appointment.date)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold" }}
                  component="p"
                >
                  Zeit
                </Typography>
                <Typography variant="body2">{appointment.timeSlot}</Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold" }}
                  component="p"
                >
                  Coach
                </Typography>
                <Typography variant="body2">
                  {appointment.coachFullName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold" }}
                  component="p"
                >
                  Status
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: getStatusColor(appointment.status) }}
                >
                  {getStatusLabel(appointment.status)}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <AppointmentStatusControls
                  appointment={appointment}
                  onChanged={fetchParticipantAppointments}
                />
              </Grid>
            </Grid>
          </Grid>

          {index < participantAppointments.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default RenderParticipantAppointments;
